import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { U2000IdCheckedBoxDisplay } from '../../../u2000Core/components/tables/display/U2000IdCheckedBoxDisplay';
import { U2000IdHeaderCheckedBoxDisplay } from '../../../u2000Core/components/tables/display/U2000IdHeaderCheckedBoxDisplay';
import { NS } from '../../U3147';
import { useU3147Context } from '../../U3147Context';
import { useU3147ManualSessionHandlers } from '../../U3147UseManualSessionHandlers';
import {
  u3147FormatProjectLabel,
  u3147FormatSubprojectLabel,
  u3147FormatTaskLabel,
  u3147IsAdmIncludeSubProjectEnabled,
  u3147IsSessionExported,
  u3147IsSessionRunning,
  type U3147TimeSession,
} from '../../U3147Service';
import { u3147FormatMinutesAsHoursMinutes, u3147FormatTimeFromIso } from '../../tools/u3147FormatDuration';
import { useU3147EnrichSessionsTasks } from '../../U3147UseEnrichSessionsTasks';
import U3147ManualSessionDialog from '../common/U3147ManualSessionDialog';
import U3147SessionDeleteDlg from '../common/U3147SessionDeleteDlg';
import U3147DayTimelineToolBar from './U3147DayTimelineToolBar';

type U3147DayTimelineRow = U3147TimeSession & { idChecked?: boolean };

function u3147IsSessionSelectable(session: U3147TimeSession): boolean {
  return !u3147IsSessionRunning(session) && !u3147IsSessionExported(session);
}

export interface U3147DayTimelineProps {
  sessions: U3147TimeSession[];
  loading?: boolean;
  selectedDate: string;
  totalMinutesFromApi?: number | null;
  onRefresh: () => void;
  onExportDay: () => void;
  exportBusy?: boolean;
}

const U3147DayTimeline: React.FC<U3147DayTimelineProps> = ({
  sessions,
  loading,
  selectedDate,
  totalMinutesFromApi,
  onRefresh,
  onExportDay,
  exportBusy,
}) => {
  const { t, i18n } = useTranslation();
  const { initLU } = useU3147Context();
  const { deleteSession } = useU3147ManualSessionHandlers();
  const [editSession, setEditSession] = useState<U3147TimeSession | null>(null);
  const [manualOpen, setManualOpen] = useState(false);
  const [deleteTargets, setDeleteTargets] = useState<U3147TimeSession[]>([]);
  const [selectionById, setSelectionById] = useState<Record<string, boolean>>({});

  const displaySessions = useU3147EnrichSessionsTasks(sessions);

  const displaySessionsKey = useMemo(
    () => displaySessions.map((session) => `${session.id}:${session.version}`).join('\n'),
    [displaySessions],
  );

  useEffect(() => {
    setSelectionById((prev) => {
      const next: Record<string, boolean> = {};
      for (const session of displaySessions) {
        next[session.id] = prev[session.id] ?? false;
      }
      return next;
    });
  }, [displaySessionsKey, displaySessions]);

  const timelineRows = useMemo<U3147DayTimelineRow[]>(
    () => displaySessions.map((session) => ({ ...session, idChecked: selectionById[session.id] ?? false })),
    [displaySessions, selectionById],
  );

  const selectableRows = useMemo(
    () => timelineRows.filter((row) => u3147IsSessionSelectable(row)),
    [timelineRows],
  );

  const allRowSelected = selectableRows.length > 0 && selectableRows.every((row) => row.idChecked === true);

  const handleSelectAllRow = (isSelected: boolean) => {
    setSelectionById((prev) => {
      const next = { ...prev };
      for (const row of timelineRows) {
        if (u3147IsSessionSelectable(row)) {
          next[row.id] = isSelected;
        }
      }
      return next;
    });
  };

  const handleRowSelectionUpdated = (row: U3147DayTimelineRow) => {
    setSelectionById((prev) => ({ ...prev, [row.id]: !!row.idChecked }));
  };

  const selectedRows = useMemo(
    () => timelineRows.filter((row) => row.idChecked === true),
    [timelineRows],
  );

  const disableToolbarEdit = selectedRows.length !== 1;
  const disableToolbarDelete = selectedRows.length === 0;

  const openAddRangeDialog = () => {
    setEditSession(null);
    setManualOpen(true);
  };

  const hasRunningSession = useMemo(() => displaySessions.some((s) => u3147IsSessionRunning(s)), [displaySessions]);

  const totalMinutes = useMemo(() => {
    if (
      !hasRunningSession &&
      typeof totalMinutesFromApi === 'number' &&
      Number.isFinite(totalMinutesFromApi)
    ) {
      return totalMinutesFromApi;
    }
    let sum = 0;
    for (const s of displaySessions) {
      if (typeof s.durationMinutes === 'number' && Number.isFinite(s.durationMinutes)) {
        sum += s.durationMinutes;
      } else if (u3147IsSessionRunning(s) && s.startedAt) {
        sum += Math.floor((Date.now() - new Date(s.startedAt).getTime()) / 60000);
      }
    }
    return sum;
  }, [displaySessions, hasRunningSession, totalMinutesFromApi]);

  const handleToolbarEdit = () => {
    const row = selectedRows[0];
    if (!row) return;
    setEditSession(row);
    setManualOpen(true);
  };

  const handleToolbarDelete = () => {
    if (selectedRows.length === 0) return;
    setDeleteTargets(selectedRows);
  };

  const clearSelection = () => {
    setSelectionById((prev) => {
      const next = { ...prev };
      for (const id of Object.keys(next)) {
        next[id] = false;
      }
      return next;
    });
  };

  const handleDeleteSelected = async () => {
    if (deleteTargets.length === 0) return;
    let deletedAny = false;
    for (const target of deleteTargets) {
      const ok = await deleteSession({ id: target.id, version: target.version });
      if (!ok) break;
      deletedAny = true;
    }
    setDeleteTargets([]);
    if (deletedAny) {
      clearSelection();
      onRefresh();
    }
  };

  const showSubprojectColumn = u3147IsAdmIncludeSubProjectEnabled(initLU.personalization);

  const footerTotalColSpan = showSubprojectColumn ? 5 : 4;

  if (loading) {
    return <div className="py-8 text-center text-sm text-gray-500">{t('lblLoading', { ns: 'common', defaultValue: 'Chargement…' })}</div>;
  }

  return (
    <div className="rounded-t-none rounded-b-lg border border-t-0 border-gray-200 bg-white">
      <div className="flex flex-wrap items-center gap-3 border-b border-gray-200 px-4 py-3">
        <div className="min-w-0 flex-1">
          <U3147DayTimelineToolBar
            onAddRange={openAddRangeDialog}
            onEdit={handleToolbarEdit}
            onDelete={handleToolbarDelete}
            onExportDay={onExportDay}
            exportBusy={exportBusy}
            disableEdit={disableToolbarEdit}
            disableDelete={disableToolbarDelete}
          />
        </div>
        <h3 className="shrink-0 text-sm font-semibold text-gray-800">
          {t('lblDayTimeline', { ns: NS, defaultValue: 'Chronologie du jour' })}
        </h3>
      </div>

      {timelineRows.length === 0 ? (
        <p className="px-4 py-8 text-center text-sm text-gray-500">
          {t('msgEmptyDay', { ns: NS, defaultValue: 'Aucune plage pour ce jour — démarrez un chronomètre ou ajoutez une plage.' })}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="w-[50px] px-2 py-2 text-center font-medium text-gray-600">
                  <U2000IdHeaderCheckedBoxDisplay
                    column={{}}
                    label=""
                    disabled={selectableRows.length === 0}
                    allRowSelected={allRowSelected}
                    handleSelectAllRow={handleSelectAllRow}
                  />
                </th>
                <th className="px-3 py-2 text-left font-medium text-gray-600">{t('colStart', { ns: NS })}</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600">{t('colEnd', { ns: NS })}</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600">{t('colDuration', { ns: NS })}</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600">{t('colProject', { ns: NS })}</th>
                {showSubprojectColumn && (
                  <th className="px-3 py-2 text-left font-medium text-gray-600">{t('colSubproject', { ns: NS })}</th>
                )}
                <th className="px-3 py-2 text-left font-medium text-gray-600">{t('colTask', { ns: NS })}</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600">{t('colNote', { ns: NS })}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {timelineRows.map((session) => {
                const running = u3147IsSessionRunning(session);
                const exported = u3147IsSessionExported(session);
                const selectable = u3147IsSessionSelectable(session);
                return (
                  <tr key={session.id} className={running ? 'bg-emerald-50/60' : undefined}>
                    <td className="w-[50px] px-2 py-2 text-center">
                      <U2000IdCheckedBoxDisplay
                        column={{}}
                        value={session.id}
                        row={session as unknown as Record<string, unknown>}
                        disabled={!selectable}
                        rowUpdated={handleRowSelectionUpdated as unknown as (row: Record<string, unknown>) => void}
                        allowRowSelection={selectable}
                      />
                    </td>
                    <td className="px-3 py-2">{u3147FormatTimeFromIso(session.startedAt)}</td>
                    <td className="px-3 py-2">
                      {running ? t('lblRunning', { ns: NS, defaultValue: 'en cours' }) : u3147FormatTimeFromIso(session.endedAt)}
                    </td>
                    <td className="px-3 py-2">{u3147FormatMinutesAsHoursMinutes(session.durationMinutes ?? undefined)}</td>
                    <td className="px-3 py-2">{u3147FormatProjectLabel(session.project as never)}</td>
                    {showSubprojectColumn && (
                      <td className="px-3 py-2">{u3147FormatSubprojectLabel(session.subproject as never)}</td>
                    )}
                    <td className="px-3 py-2">{u3147FormatTaskLabel(session.task as never, i18n.language)}</td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap items-center gap-2">
                        {exported && (
                          <span className="rounded-sm bg-gray-200 px-2 py-0.5 text-xs font-medium text-gray-700">
                            {t('lblExported', { ns: NS, defaultValue: 'Exportée' })}
                          </span>
                        )}
                        <span>{session.note ?? ''}</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="bg-gray-50 font-medium">
                <td colSpan={3} className="px-3 py-2 text-right">
                  {t('lblTotalDay', { ns: NS })}
                </td>
                <td className="px-3 py-2" colSpan={footerTotalColSpan}>
                  {u3147FormatMinutesAsHoursMinutes(totalMinutes)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      <U3147ManualSessionDialog
        isOpen={manualOpen}
        onClose={() => {
          setManualOpen(false);
          setEditSession(null);
        }}
        onSaved={() => {
          clearSelection();
          onRefresh();
        }}
        defaultDate={selectedDate}
        session={editSession}
        existingSessions={sessions}
      />

      <U3147SessionDeleteDlg
        isOpen={deleteTargets.length > 0}
        sessions={deleteTargets}
        onClose={(result) => {
          if (result === 'yes') {
            void handleDeleteSelected();
          } else {
            setDeleteTargets([]);
          }
        }}
      />
    </div>
  );
};

export default U3147DayTimeline;
